(function () {
  if (window.ReconCartDrawer) return;

  const root = document.querySelector('[data-cart-drawer]');
  if (!root) return;

  const panel = root.querySelector('[data-cart-drawer-panel]');
  const content = root.querySelector('[data-cart-drawer-content]');
  const sectionId = root.dataset.sectionId || 'static-cart';
  const openOnAdd = root.dataset.openOnAdd !== 'false';
  const quantityTimers = new Map();

  let lastFocusedElement = null;
  let focusables = [];

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const cartPageActive = () => document.body.classList.contains('template-cart');

  const buildRequestUrl = (path, params) => {
    const url = new URL(path, window.location.origin);

    Object.entries(params || {}).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });

    url.searchParams.set('_', Date.now().toString());

    return url.toString();
  };

  const loadDrawerHtml = async () => {
    const response = await fetch(buildRequestUrl(window.Theme.routes.cart_url, {
      section_id: sectionId
    }), {
      credentials: 'same-origin'
    });

    if (!response.ok) {
      throw new Error('Unable to load cart drawer.');
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fragment = doc.querySelector('[data-html="cart_drawer"]');

    if (!fragment) {
      throw new Error('Cart drawer content was not found.');
    }

    return fragment.innerHTML;
  };

  const updateFocusableElements = () => {
    focusables = Array.from(panel.querySelectorAll(focusableSelector)).filter((element) => {
      return element.offsetParent !== null;
    });
  };

  const updateQuantityButtonState = (input) => {
    if (!input) return;

    const wrapper = input.closest('.quantity-selector__wrapper');
    if (!wrapper) return;

    const minusWrapper = wrapper.querySelector('[data-button-wrapper-minus]');
    const value = parseInt(input.value, 10) || 1;

    if (minusWrapper) {
      minusWrapper.classList.toggle('quantity-selector__button-wrapper--disabled', value <= 1);
    }
  };

  const updateAllQuantityButtonStates = (scope) => {
    scope.querySelectorAll('[data-quantity-input]').forEach((input) => {
      updateQuantityButtonState(input);
    });
  };

  const setBusy = (isBusy) => {
    if (!content) return;

    if (isBusy) {
      content.setAttribute('aria-busy', 'true');
    } else {
      content.removeAttribute('aria-busy');
    }
  };

  const refresh = async () => {
    setBusy(true);

    try {
      content.innerHTML = await loadDrawerHtml();
      updateAllQuantityButtonStates(content);
      updateFocusableElements();
    } catch (error) {
      window.location.href = window.Theme.routes.cart_url;
    } finally {
      setBusy(false);
    }
  };

  const open = async (sourceElement, shouldRefresh = true) => {
    if (cartPageActive()) {
      window.location.href = window.Theme.routes.cart_url;
      return;
    }

    lastFocusedElement = sourceElement || document.activeElement;
    root.classList.add('is-active');
    root.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('cart-drawer-visible');
    document.body.classList.add('cart-drawer-visible');

    if (shouldRefresh) {
      await refresh();
    } else {
      updateFocusableElements();
    }

    const initialFocusTarget = root.querySelector('[data-cart-drawer-initial-focus]') || focusables[0];
    if (initialFocusTarget) {
      window.requestAnimationFrame(() => initialFocusTarget.focus());
    }
  };

  const close = () => {
    root.classList.remove('is-active');
    root.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('cart-drawer-visible');
    document.body.classList.remove('cart-drawer-visible');

    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus();
    }
  };

  const dispatchCartUpdate = (cart) => {
    window.dispatchEvent(new CustomEvent('cartcount:update', {
      detail: cart
    }));
  };

  const changeItemQuantity = async (key, quantity) => {
    const response = await fetch(`${window.Theme.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: key,
        quantity
      })
    });

    const cart = await response.json();

    if (!response.ok || cart.status) {
      throw new Error(cart.description || 'We could not update your cart.');
    }

    dispatchCartUpdate(cart);
    await refresh();
  };

  const scheduleQuantityChange = (key, quantity) => {
    if (quantityTimers.has(key)) {
      window.clearTimeout(quantityTimers.get(key));
    }

    const timeoutId = window.setTimeout(() => {
      quantityTimers.delete(key);
      changeItemQuantity(key, quantity).catch(() => {
        window.location.href = window.Theme.routes.cart_url;
      });
    }, 250);

    quantityTimers.set(key, timeoutId);
  };

  const onDocumentClick = (event) => {
    const trigger = event.target.closest('[data-cart-drawer-trigger]');
    if (trigger) {
      if (cartPageActive() || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      event.preventDefault();
      open(trigger);
      return;
    }

    if (!root.classList.contains('is-active')) return;

    if (event.target.closest('[data-cart-drawer-close]')) {
      event.preventDefault();
      close();
      return;
    }

    const removeTrigger = event.target.closest('[data-cartitem-remove]');
    if (removeTrigger && root.contains(removeTrigger)) {
      event.preventDefault();
      const row = removeTrigger.closest('[data-cartitem-key]');
      if (!row) return;
      changeItemQuantity(row.getAttribute('data-cartitem-key'), 0).catch(() => {
        window.location.href = window.Theme.routes.cart_url;
      });
      return;
    }

    const quantityButton = event.target.closest('[data-quantity-minus], [data-quantity-plus]');
    if (quantityButton && root.contains(quantityButton)) {
      event.preventDefault();
      const wrapper = quantityButton.closest('.quantity-selector__wrapper');
      const row = quantityButton.closest('[data-cartitem-key]');
      const input = wrapper ? wrapper.querySelector('[data-quantity-input]') : null;

      if (!wrapper || !row || !input) return;

      const increment = parseInt(input.getAttribute('data-increment-value'), 10) || 1;
      const currentValue = parseInt(input.value, 10) || 1;
      const nextValue = quantityButton.hasAttribute('data-quantity-minus')
        ? Math.max(1, currentValue - increment)
        : currentValue + increment;

      input.value = nextValue;
      updateQuantityButtonState(input);
      scheduleQuantityChange(row.getAttribute('data-cartitem-key'), nextValue);
    }
  };

  const onDocumentChange = (event) => {
    const input = event.target.closest('[data-quantity-input]');
    if (!input || !root.contains(input)) return;

    const row = input.closest('[data-cartitem-key]');
    if (!row) return;

    const nextValue = Math.max(1, parseInt(input.value, 10) || 1);
    input.value = nextValue;
    updateQuantityButtonState(input);
    scheduleQuantityChange(row.getAttribute('data-cartitem-key'), nextValue);
  };

  const onKeydown = (event) => {
    if (!root.classList.contains('is-active')) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key !== 'Tab') return;

    updateFocusableElements();

    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  document.addEventListener('click', onDocumentClick);
  document.addEventListener('change', onDocumentChange);
  document.addEventListener('keydown', onKeydown);

  updateAllQuantityButtonStates(content);

  window.ReconCartDrawer = {
    open(sourceElement) {
      return open(sourceElement, true);
    },
    close,
    refresh,
    openFromCartUpdate(cart, options = {}) {
      if (cartPageActive()) return;

      if (!openOnAdd) {
        if (root.classList.contains('is-active')) {
          refresh();
        }
        return;
      }

      open(options.activeElement || document.activeElement, true);
    }
  };
})();
